import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useCalendar } from '../contexts/CalendarContext';
import { useNotification } from '../contexts/NotificationContext';
import { useSettings } from '../contexts/SettingsContext';
import { useAuth } from '../contexts/AuthContext';
import { playPresetSound, DEFAULT_SOUND_ID } from '../utils/soundPresets';
import { format, isSameDay, parse, addDays } from 'date-fns';
import { decideNotificationPolicy } from '../notifications/NotificationPolicy';
import { getAppState } from '../utils/appState';
import { getLockActive } from '../utils/lockActiveStore';
import i18n from '../i18n';

const REMINDER_STORAGE_PREFIX = 'reminder_';

/** Normalize time string to HH:mm (e.g. "0:37" -> "00:37") for reliable comparison. */
function normalizeTimeToHHmm(value) {
  if (!value || typeof value !== 'string') return '09:00';
  const parts = value.trim().split(':');
  const h = Math.max(0, parseInt(parts[0], 10) || 0);
  const m = Math.max(0, Math.min(59, parseInt(parts[1], 10) || 0));
  return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
}

/** Returns true if now is the reminder time for this event (reminderDay + reminderHour). */
export function isReminderTime(event, now = new Date()) {
  const reminderDay = parseInt(event.reminderDay || '0', 10);
  const rawHour = event.allDay ? (event.reminderHour || '09:00') : (event.reminderHour || '09:00');
  const reminderHour = normalizeTimeToHHmm(rawHour);

  const startStr = event.startDate ? String(event.startDate).slice(0, 10) : '';
  if (!startStr || startStr.length < 10) return false;
  const eventDate = parse(startStr, 'yyyy-MM-dd', new Date());
  if (Number.isNaN(eventDate.getTime())) return false;

  let reminderDate = new Date(eventDate);
  if (reminderDay > 0) {
    reminderDate = addDays(reminderDate, -reminderDay);
  }
  if (!isSameDay(reminderDate, now)) return false;
  const currentTime = format(now, 'HH:mm');
  return reminderHour === currentTime;
}

const useCalendarNotifications = () => {
  const { t } = useTranslation('shared');
  const { events, calendars } = useCalendar();
  const { addNotification } = useNotification();
  const { preferences } = useSettings();
  const { isPlusUser, user } = useAuth();

  useEffect(() => {
    function runReminderCheck() {
      if (preferences.showCalendarNotifications === false) {
        return;
      }

      const now = new Date();
      const currentDate = format(now, 'yyyy-MM-dd');
      const currentTime = format(now, 'HH:mm');

      events.forEach((event) => {
        const calendar = calendars.find((cal) => cal.id === event.calendarId);
        if (!calendar) return;

        if (!isReminderTime(event, now)) return;

        // Aynı event için birden fazla tetikleme olmasın: localStorage key reminder_eventId_timestamp
        const storageKey = `${REMINDER_STORAGE_PREFIX}${event.id}_${currentDate}_${currentTime}`;
        try {
          if (localStorage.getItem(storageKey)) return;
          localStorage.setItem(storageKey, String(Date.now()));
        } catch (e) {
          return;
        }
          // Get notification policy
          const appState = getAppState();
          const policy = decideNotificationPolicy({
            type: 'calendar',
            appState,
          });

          // Bildirim içeriği (generic when app lock active)
          const locked = getLockActive(user?.id);
          const title = locked
            ? i18n.t('lock:notifications.calendarTitle')
            : event.allDay
              ? t('calendarNotification.allDayTitle', { title: event.title })
              : t('calendarNotification.timedTitle', { title: event.title, time: event.startTime });

          const message = locked
            ? i18n.t('lock:notifications.calendarBody')
            : event.description
              ? event.description
              : event.allDay
                ? t('calendarNotification.allDayMessage')
                : t('calendarNotification.timedMessage', { time: event.startTime });

          // Apply policy decisions
          if (policy.showDropdown && !locked) {
            addNotification({
              type: 'calendar',
              title,
              message,
              data: { event, calendar },
            });
          }

          if (policy.showToast && !locked) {
            // Show toast notification
            if (typeof window !== 'undefined') {
              window.dispatchEvent(new CustomEvent('notesplus-show-toast', {
                detail: {
                  type: 'calendar',
                  title,
                  message,
                },
              }));
            }
          }

          if (policy.showSystem) {
            // Show Windows/system notification
            if (window.api?.showNotification) {
              try {
                window.api.showNotification({
                  title,
                  body: message,
                  sound: policy.playSound && preferences.playNotificationSound !== false,
                  meta: {
                    type: 'calendar',
                    eventId: event.id,
                  },
                });
              } catch (error) {
                console.error('Takvim bildirimi gösterilirken hata:', error);
              }
            } else if ('Notification' in window && Notification.permission === 'granted') {
              try {
                new Notification(title, {
                  body: message,
                  icon: '/favicon.ico',
                });
              } catch (error) {
                console.error('HTML5 bildirimi gösterilirken hata:', error);
              }
            }
          }

          if (preferences.playNotificationSound !== false && policy.playSound) {
            try {
              // Plus üyeler için varsayılan olarak Plus ses preset'i kullan
              let soundId =
                preferences.calendarNotificationSoundId ||
                preferences.notificationSoundId;
              if (!soundId) {
                // Plus üye ise varsayılan Plus sesi, değilse standart ses
                soundId = isPlusUser ? 'plus-royal' : DEFAULT_SOUND_ID;
              }
              playPresetSound(soundId);
            } catch (error) {
              console.error('Takvim bildirimi sesi çalınamadı:', error);
            }
          }

      });
    }

    runReminderCheck(); // İlk kontrol hemen (30 sn beklemeden)
    const checkInterval = setInterval(runReminderCheck, 30000);

    return () => {
      clearInterval(checkInterval);
    };
  }, [
    events,
    calendars,
    addNotification,
    preferences.showCalendarNotifications,
    preferences.playNotificationSound,
    preferences.calendarNotificationSoundId,
    preferences.notificationSoundId,
    user?.id,
  ]);
};

export default useCalendarNotifications;




